import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Circle,
  VideoOff,
} from "lucide-react";

const QuestionDisplay = ({
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  answer,
  onAnswerChange,
  onNavigate,
  onSubmit,
  faceDetectionEnabled,
  faceDetected,
  submitLoading,
}) => {
  if (!currentQuestion) {
    return (
      <Card className="p-6">
        <div className="text-center py-8 text-gray-500">No question loaded</div>
      </Card>
    );
  }

  // Enhanced answer normalization with type checking
  const normalizedAnswers = Array.isArray(currentQuestion.answers)
    ? currentQuestion.answers
    : typeof currentQuestion.answers === "string"
    ? currentQuestion.answers.split(",").map(a => a.trim())
    : [];

  // More reliable question type detection
  // SIMPLIFIED TYPE DETECTION
const isMultipleAnswerQuestion = 
  currentQuestion.qtyp === 'MULTIPLE_CHOICE' || 
  (currentQuestion.answers?.length > 1); // Fallback

  useEffect(() => {
    console.log(`Current question type:`, isMultipleAnswerQuestion ? "Multiple" : "Single");
    console.log(`Correct answers:`, normalizedAnswers);
  }, [currentQuestion, isMultipleAnswerQuestion]);

  const handleAnswerSelection = (selectedAnswer) => {
    if (faceDetectionEnabled && !faceDetected) return;

    const currentAnswers = Array.isArray(answer) 
      ? answer.filter(a => a !== "")
      : answer 
      ? [answer] 
      : [];

    if (isMultipleAnswerQuestion) {
      const newAnswers = currentAnswers.includes(selectedAnswer)
        ? currentAnswers.filter(a => a !== selectedAnswer)
        : [...currentAnswers, selectedAnswer];
      onAnswerChange(newAnswers.length > 0 ? newAnswers : "");
    } else {
      onAnswerChange(currentAnswers[0] === selectedAnswer ? "" : selectedAnswer);
    }
  };

  const isChoiceSelected = (choice) => {
    const currentAnswers = Array.isArray(answer) 
      ? answer 
      : answer 
      ? [answer] 
      : [];
    return currentAnswers.includes(choice);
  };

  return (
    <Card className="p-6">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold flex-1">
            Q{currentQuestionIndex + 1}: {currentQuestion?.question}
          </h2>
          <div className="flex gap-2 ml-4">
            {isMultipleAnswerQuestion ? (
              <Badge variant="secondary" className="flex items-center gap-1">
                <CheckSquare className="h-3 w-3" />
                Multiple Answers
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <Circle className="h-3 w-3" />
                Single Answer
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Instructions */}
      <div className="mb-6">
        {isMultipleAnswerQuestion ? (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700 font-medium">
              ℹ️ Select <strong>all correct answers</strong>
            </p>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800 font-medium">
              🛈 Select the <strong>one correct answer</strong>
            </p>
          </div>
        )}

        {/* Optimized Choices Rendering */}
        <div className="space-y-3">
          {Array.isArray(currentQuestion.choices) &&
            currentQuestion.choices.map((choice, index) => {
              const isSelected = isChoiceSelected(choice);
              const choiceKey = `${currentQuestionIndex}-${index}`;
              
              return (
                <div
                  key={choiceKey}
                  className={`p-3 border rounded-md transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "hover:bg-gray-50 border-gray-200"
                  } ${
                    faceDetectionEnabled && !faceDetected
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onClick={() => handleAnswerSelection(choice)}
                >
                  <label className="flex items-center cursor-pointer">
                    <input
                      type={isMultipleAnswerQuestion ? "checkbox" : "radio"}
                      checked={isSelected}
                      readOnly
                      className={`mr-3 ${
                        isMultipleAnswerQuestion ? "rounded" : "rounded-full"
                      } border-gray-300 text-primary focus:ring-primary h-4 w-4`}
                    />
                    <span className="text-sm">{choice}</span>
                  </label>
                </div>
              );
            })}
        </div>

        {/* Face detection warning */}
        {faceDetectionEnabled && !faceDetected && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm flex items-center">
            <VideoOff className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>
              Your face must be visible to interact with the exam. Please
              position yourself in front of the camera.
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => onNavigate(currentQuestionIndex - 1)}
          disabled={
            currentQuestionIndex === 0 ||
            (faceDetectionEnabled && !faceDetected)
          }
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        <div className="flex gap-2">
          {currentQuestionIndex === totalQuestions - 1 ? (
            <Button
              onClick={onSubmit}
              disabled={submitLoading || (faceDetectionEnabled && !faceDetected)}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitLoading ? "Submitting..." : "Submit Exam"}
            </Button>
          ) : (
            <Button
              onClick={() => onNavigate(currentQuestionIndex + 1)}
              disabled={faceDetectionEnabled && !faceDetected}
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default QuestionDisplay;